import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, AlertOctagon, FileCheck, History, ShieldAlert, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useAdmin } from "./useAdmin";
import { useSeo, PRIVATE_SEO } from "@/lib/seo";

function MetricTile({ label, value, hint, to, testId }) {
  const Comp = to ? Link : "div";
  const extra = to ? { to } : {};
  return (
    <Comp {...extra} className="adm-card group block p-5 transition-colors hover:border-[#9CA3AF]" data-testid={testId}>
      <p className="adm-label">{label}</p>
      <p className="mt-3 text-3xl font-semibold text-admin-text">{value}</p>
      {hint && <p className="mt-1 text-xs text-admin-muted">{hint}</p>}
    </Comp>
  );
}

export default function AdminDashboardPage() {
  useSeo({ title: "Staff dashboard | LUX", ...PRIVATE_SEO });
  const { me, can } = useAdmin();
  const { data } = useQuery({
    queryKey: ["admin", "dashboard"],
    queryFn: async () => (await api.get("/admin/dashboard")).data,
    staleTime: 15_000,
  });

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <header>
        <p className="adm-label">Overview</p>
        <h1 className="mt-2 text-3xl font-semibold text-admin-text" data-testid="admin-dashboard-title">Welcome, {me?.name?.split(" ")[0]}</h1>
        <p className="mt-1 text-sm text-admin-muted">You're signed in as <strong className="text-admin-text">{me?.role_label}</strong>. Every action you take is written to the append-only audit log.</p>
      </header>

      {can("cases.view") && (
        <section>
          <h2 className="text-sm font-semibold text-admin-text">Moderation queues</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <MetricTile label="Normal queue" value={data?.queue_normal ?? "—"} to="/admin/cases?queue=normal" testId="metric-queue-normal" hint="Routine reviews & edits" />
            <MetricTile label="Priority queue" value={data?.queue_priority ?? "—"} to="/admin/cases?queue=priority" testId="metric-queue-priority" hint="High-risk signals" />
            {can("cases.compliance") && (
              <MetricTile label="Compliance queue" value={data?.queue_compliance ?? "—"} to="/admin/cases?queue=compliance" testId="metric-queue-compliance" hint="Minors, coercion, holds" />
            )}
            <MetricTile label="Reports (24h)" value={data?.reports_24h ?? "—"} testId="metric-reports" hint="Visitor reports received" />
          </div>
        </section>
      )}

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {can("cases.view") && <MetricTile label="Live profiles" value={data?.live_profiles ?? "—"} testId="metric-live" />}
        {can("cases.view") && <MetricTile label="Stories pending" value={data?.stories_pending ?? "—"} testId="metric-stories" />}
        {can("appeals.review") && <MetricTile label="Appeals open" value={data?.appeals_open ?? "—"} to="/admin/appeals" testId="metric-appeals" />}
        {can("bans.manage") && <MetricTile label="Active bans" value={data?.bans_active ?? "—"} testId="metric-bans" />}
        {can("tickets.manage") && <MetricTile label="Open tickets" value={data?.tickets_open ?? "—"} testId="metric-tickets" />}
        {can("staff.manage") && <MetricTile label="Staff active" value={data?.staff_active ?? "—"} to="/admin/staff" testId="metric-staff" />}
        {can("evidence.export") && <MetricTile label="Export requests" value={data?.exports_pending ?? "—"} testId="metric-exports" />}
      </section>

      <section className="adm-card p-5">
        <div className="flex items-start gap-3">
          <ShieldAlert className="mt-1 h-5 w-5 text-[#9A6B12]" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold text-admin-text">Reminder</p>
            <p className="mt-1 text-sm text-admin-muted">
              Automated scoring never bans anyone. Only trained staff can suspend or ban. Reports about possible minors or coercion go straight to Compliance.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
